import React, { useRef, useLayoutEffect, useEffect, useState } from "react";

const CSS_FONT_FAMILIES = {
  arial: "ArialCustom, sans-serif",
  times: "TimesCustom, serif",
  courier: "CourierCustom, monospace",
  verdana: "VerdanaCustom, sans-serif",
  roboto: "RobotoCustom, sans-serif",
  comic: "ComicCustom, sans-serif",
  opensans: "OpenSansCustom, sans-serif",
};

const AutoResizeTextBox = ({
  field,
  updateField,
  selectField,
  isSelected,
  onContainerMouseDown,
  onContainerMouseUp,
  zoom,
  onEditStart,
  onEditEnd,
}) => {
  const textareaRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const updateSize = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    ta.style.width = "auto";
    ta.style.height = "auto";

    const baseSize = field.size ?? 16;
    const fontSizePx = baseSize * zoom;
    ta.style.fontSize = `${fontSizePx}px`;
    ta.style.lineHeight = `${fontSizePx}px`;

    const scrollW = ta.scrollWidth;
    const scrollH = ta.scrollHeight;
    const bufferPx = 2;

    const desiredWidthPx = (field.text ? scrollW : fontSizePx) + bufferPx;
    const desiredHeightPx = field.text ? scrollH : fontSizePx;

    const newWidth = desiredWidthPx / zoom;
    const newHeight = desiredHeightPx / zoom;

    if (newWidth !== field.width || newHeight !== field.height) {
      updateField(field.id, {
        width: newWidth,
        height: newHeight,
      });
    }

    ta.style.width = `${desiredWidthPx}px`;
    ta.style.height = `${desiredHeightPx}px`;
  };

  useLayoutEffect(updateSize, [
    field.text,
    field.size,
    field.fontFamily,
    field.bold,
    field.italic,
    field.underline,
    field.textAlign,
    field.color,
    field.fillColor,
    field.height,
    field.id,
    field.width,
    updateField,
    zoom,
  ]);

  useEffect(() => {
    if (isSelected && isEditing && textareaRef.current) {
      const ta = textareaRef.current;
      const len = ta.value.length;
      ta.setSelectionRange(len, len);
      ta.focus();
    }
  }, [isSelected, isEditing]);

  const handleChange = (e) => {
    updateField(field.id, { text: e.target.value });
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
    onEditStart?.();
  };

  const handleBlur = () => {
    setIsEditing(false);
    onEditEnd?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      textareaRef.current?.blur();
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    selectField?.(field.id);
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    onContainerMouseDown?.(e, field.id);
  };

  const handleMouseUp = (e) => {
    e.stopPropagation();
    onContainerMouseUp?.(e);
    selectField?.(field.id);
  };

  return (
    <div
      style={{
        position: "absolute",
        top: (field.baseTop - (field.height ?? field.baseHeight) / 2) * zoom,
        left: field.baseLeft * zoom,
        width: `${(field.width ?? field.baseWidth) * zoom}px`,
        height: `${(field.height ?? field.baseHeight) * zoom}px`,
        boxSizing: "border-box",
      }}
      onMouseDown={(e) => {
        if (!isEditing) handleMouseDown(e);
      }}
      onClick={handleClick}
      onMouseUp={onContainerMouseUp}
      onDoubleClick={handleDoubleClick}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseUp={handleMouseUp}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: "100%",
          height: "100%",
          border:
            isSelected || isHovered
              ? "1px dashed #60B5FF"
              : "1px solid transparent",
          background: "transparent",
          cursor: isEditing ? "text" : "move",
        }}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={field.text}
          placeholder=""
          wrap="off"
          readOnly={!isEditing}
          onChange={handleChange}
          onBlur={handleBlur}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%",
            height: "100%",
            padding: 0,
            margin: 0,
            boxSizing: "border-box",
            resize: "none",
            overflow: "hidden",
            border: "none",
            outline: "none",
            background: field.fillColor || "transparent",
            color: field.color || "#000",
            textAlign: field.textAlign || "left",
            fontFamily:
              CSS_FONT_FAMILIES[field.fontFamily?.toLowerCase()] ||
              "sans-serif",
            fontSize: `${(field.size ?? 16) * zoom}px`,
            lineHeight: `${(field.size ?? 16) * zoom}px`,
            fontWeight: field.bold ? 700 : 400,
            fontStyle: field.italic ? "italic" : "normal",
            textDecoration: field.underline ? "underline" : "none",
            whiteSpace: "pre",
            overflowWrap: "normal",
            wordBreak: "keep-all",
            pointerEvents: isEditing ? "auto" : "none",
          }}
        />
      </div>
    </div>
  );
};

export default AutoResizeTextBox;
